import React, { useState, useEffect } from 'react'
import { useHospitalSelection } from '../../hooks/useHospitalSelection'
import { useHospitalData } from '../../hooks/useHospitalData'
import MultiSelectNotice from '../../components/Common/MultiSelectNotice'
import NoDataOverlay from '../../components/Common/NoDataOverlay'
import LoadingNotice from '../../components/LoadingNotice/LoadingNotice'
import html2canvas from 'html2canvas'
import { generatePDFIds } from '../../utils/pdfIdHelper'
import './StructureMeasures.css'

const StructureMeasures: React.FC = () => {
    const { selectedHospitalIds, isMultiSelect } = useHospitalSelection()

    const { data, loading } = useHospitalData(
        '/api/pmh/structure_measures',
        isMultiSelect ? [] : selectedHospitalIds
    )

    const [statusMap, setStatusMap] = useState<Record<string, number>>({})
    const [reportingMonth, setReportingMonth] = useState<string>('NA')
    const [hasData, setHasData] = useState<boolean>(true)

    const expandMonthName = (monthStr: string): string => {
        if (!monthStr) return "";
        const monthMap: Record<string, string> = {
            Jan: "January", Feb: "February", Mar: "March", Apr: "April",
            May: "May", Jun: "June", Jul: "July", Aug: "August",
            Sep: "September", Oct: "October", Nov: "November", Dec: "December"
        };

        if (monthStr.startsWith("Baseline ")) {
            const rest = monthStr.replace("Baseline ", "").trim();
            const [abbr, year] = rest.split(" ");
            const fullMonth = monthMap[abbr] || abbr;
            return `Baseline ${fullMonth}${year ? ` ${year}` : ""}`;
        }

        const [abbr, year] = monthStr.split(" ");
        if (abbr && year) {
            const fullMonth = monthMap[abbr] || abbr;
            return `${fullMonth} ${year}`;
        }
        return monthStr;
    };

    useEffect(() => {
        if (isMultiSelect || !data) return

        let rows: any[] = []
        if (data?.response?.result?.rows) rows = data.response.result.rows
        else if (data?.result?.rows) rows = data.result.rows
        else if (data?.rows) rows = data.rows
        else if (Array.isArray(data)) rows = data

        if (rows.length > 0) {
            const month = rows[0][1] || "NA"
            setReportingMonth(expandMonthName(month))

            const newStatusMap: Record<string, number> = {}
            rows.forEach((row: any[]) => {
                const fieldUsed = row[3]
                const status = parseInt(row[4])
                if (fieldUsed) {
                    newStatusMap[fieldUsed] = status
                }
            })
            setStatusMap(newStatusMap)
            setHasData(true)
        } else {
            setReportingMonth("NA")
            setStatusMap({})
            setHasData(false)
        }
    }, [data, isMultiSelect])

    const getStatusClass = (field: string) => {
        const val = statusMap[field]
        if (val === 3) return 'green'
        if (val === 2) return 'yellow'
        return 'orange'
    }

    const tableContainerRef = React.useRef<HTMLDivElement>(null)



    const toggleMenu = (e: React.MouseEvent) => {
        e.stopPropagation();
        const menu = document.getElementById('export-menu-structure');
        if (menu) {
            menu.classList.toggle('show');
        }
    };

    const closeMenu = () => {
        const menu = document.getElementById('export-menu-structure');
        if (menu) menu.classList.remove('show');
    };

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (!(e.target as Element).closest('.export-menu-container')) {
                closeMenu();
            }
        };
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    const handleDownloadPNG = async () => {
        if (tableContainerRef.current) {
            try {
                const wrapper = document.createElement('div')
                wrapper.style.position = 'absolute'
                wrapper.style.left = '-9999px'
                wrapper.style.top = '-9999px'
                wrapper.style.width = tableContainerRef.current.offsetWidth + 'px'
                wrapper.style.backgroundColor = '#ffffff'
                wrapper.style.padding = '20px'

                const title = document.createElement('div')
                title.style.fontFamily = "'Segoe UI', sans-serif"
                title.style.fontSize = '24px'
                title.style.fontWeight = '700'
                title.style.color = '#000000'
                title.style.marginBottom = '6px'
                title.textContent = 'Structure Measures'

                const subtitle = document.createElement('div')
                subtitle.style.fontFamily = "'Segoe UI', sans-serif"
                subtitle.style.fontSize = '16px'
                subtitle.style.color = '#444'
                subtitle.style.marginBottom = '16px'
                subtitle.textContent = `Reporting Month: ${reportingMonth || 'NA'}`

                wrapper.appendChild(title)
                wrapper.appendChild(subtitle)

                const clone = tableContainerRef.current.cloneNode(true) as HTMLElement
                wrapper.appendChild(clone)
                document.body.appendChild(wrapper)

                clone.querySelectorAll<HTMLElement>('th, td').forEach((cell) => {
                    cell.style.fontSize = '18px'
                    cell.style.padding = '18px 20px'
                    cell.style.lineHeight = '1.7'
                })

                const canvas = await html2canvas(wrapper, {
                    scale: 3,
                    useCORS: true,
                    backgroundColor: '#ffffff',
                    ignoreElements: (el: Element) => el.classList.contains('export-ignore')
                } as any)

                document.body.removeChild(wrapper)

                const link = document.createElement('a')
                link.download = 'Structure_Measures.png'
                link.href = canvas.toDataURL('image/png')
                link.click()
            } catch (error) {
                console.error('Error generating PNG:', error)
            }
        }
    }

    const handleDownloadSVG = async () => {
        if (tableContainerRef.current) {
            try {
                const wrapper = document.createElement('div')
                wrapper.style.position = 'absolute'
                wrapper.style.left = '-9999px'
                wrapper.style.top = '-9999px'
                wrapper.style.width = tableContainerRef.current.offsetWidth + 'px'
                wrapper.style.backgroundColor = '#ffffff'
                wrapper.style.padding = '20px'

                const title = document.createElement('div')
                title.style.fontFamily = "'Segoe UI', sans-serif"
                title.style.fontSize = '24px'
                title.style.fontWeight = '700'
                title.style.color = '#000000'
                title.style.marginBottom = '6px'
                title.textContent = 'Structure Measures'

                const subtitle = document.createElement('div')
                subtitle.style.fontFamily = "'Segoe UI', sans-serif"
                subtitle.style.fontSize = '16px'
                subtitle.style.color = '#444'
                subtitle.style.marginBottom = '16px'
                subtitle.textContent = `Reporting Month: ${reportingMonth || 'NA'}`

                wrapper.appendChild(title)
                wrapper.appendChild(subtitle)

                const clone = tableContainerRef.current.cloneNode(true) as HTMLElement
                wrapper.appendChild(clone)
                document.body.appendChild(wrapper)

                clone.querySelectorAll<HTMLElement>('th, td').forEach((cell) => {
                    cell.style.fontSize = '18px'
                    cell.style.padding = '18px 20px'
                    cell.style.lineHeight = '1.7'
                })

                const canvas = await html2canvas(wrapper, {
                    scale: 3,
                    useCORS: true,
                    backgroundColor: '#ffffff',
                    ignoreElements: (el: Element) => el.classList.contains('export-ignore')
                } as any)

                document.body.removeChild(wrapper)

                const width = canvas.width / 3;
                const height = canvas.height / 3;
                const dataURL = canvas.toDataURL('image/png');

                const svgContent = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
  <rect width="100%" height="100%" fill="white"/>
  <image x="0" y="0" width="${width}" height="${height}" href="${dataURL}" preserveAspectRatio="xMidYMid meet"/>
</svg>`;

                const blob = new Blob([svgContent], { type: 'image/svg+xml;charset=utf-8' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = 'Structure_Measures.svg';
                link.click();
                URL.revokeObjectURL(url);
            } catch (error) {
                console.error('Error generating SVG:', error);
            }
        }
    };

    const handleDownloadCSV = () => {
        if (!data) return;

        const rowsStub = [
            { main: "Provider Education & Engagement", sub: "OB Provider Education", desc: "Educate all OB clinical staff caring for pregnant/postpartum patients on PMH conditions, screening, treatment, referral processes, hotline resources, and trauma informed care.", key: "ob_education" },
            { main: "", sub: "ER Provider Education", desc: "Educate all ER clinical staff caring for pregnant/postpartum patients on PMH conditions, screening, treatment, referral processes, hotline resources, and trauma informed care.", key: "ed_education" },
            { main: "", sub: "PMH Access Workgroup", desc: "Create a PMH Access Workgroup with multidisciplinary inpatient and outpatient care team members (OB, ED, Peds, social work, psychiatry, etc.) and community stakeholders to identify barriers and enhance coordination.", key: "multidisciplinary_workgroup" },
            { main: "Patient & Community Education and Engagement", sub: "Patient Education", desc: "Have a process to provide postpartum patient education on PMH conditions, warning signs, and follow-up options including hotlines/warm lines.", key: "patient_education" },
            { main: "", sub: "Sharing patient education outpatient", desc: "Have a process to share PMH patient education resources with outpatient clinical settings.", key: "share_education" },
            { main: "", sub: "Patient Partner", desc: "Identify a patient partner for hospital PMH quality improvement team and involve them in engagement strategies.", key: "patient_partner" },
            { main: "Screening & Assessment", sub: "Screening Workflow", desc: "Establish protocol and workflow to screen all OB patients for PMH conditions with validated tools during delivery hospitalization.", key: "screening" },
            { main: "", sub: "Sharing screening and assessments outpatient", desc: "Share validated PMH screening tools and treatment algorithms with outpatient settings (FQHCs, emergency departments, prenatal clinics, etc.).", key: "share_screening" },
            { main: "Optimizing Treatment", sub: "Treatment Process Flow", desc: "Establish process flow for treating positive PMH screens during delivery hospitalization, including use of IL DocAssist for consultation.", key: "treating" },
            { main: "Linkage to Resources & Treatment", sub: "Linkage To Follow-up Process Flow", desc: "Establish process for referral and linkage to behavioral health services for positive PMH screens during delivery, using IL DocAssist and IL MOMS Line.", key: "referral_to_link" },
            { main: "", sub: "Innovative Strategies", desc: "Identify and implement innovative strategies to address barriers to behavioral health follow-up care for patients with both private and Medicaid insurance.", key: "innovative_strategies" },
            { main: "Optimize SUD Care", sub: "SUD Treatment Linkage OB", desc: "Establish process for linkage to recovery treatment services for positive perinatal SUD screens in OB settings, including utilization of IL DocAssist and MAR Now.", key: "ob_sud_linkage" },
            { main: "", sub: "SUD Treatment Linkage ER", desc: "Establish process for linkage to recovery treatment services for positive perinatal SUD screens in ER settings, including utilization of IL DocAssist and MAR Now.", key: "er_sud_linkage" },
            { main: "", sub: "DOPP Participation", desc: "Improve risk reduction for SUD through participation in DOPP and distribution of naloxone/Narcan kits for pregnant/postpartum patients at risk of overdose.", key: "dopp" }
        ];

        let csvContent = "data:text/csv;charset=utf-8,Key Drivers,Key System Changes,Description,Status\n";

        rowsStub.forEach(row => {
            const statusVal = statusMap[row.key];
            let statusText = "Haven't started";
            if (statusVal === 3) statusText = "In place";
            else if (statusVal === 2) statusText = "Working on it";
            else if (!statusVal) statusText = "N/A";

            csvContent += `"${row.main}","${row.sub}","${row.desc.replace(/"/g, '""')}","${statusText}"\n`;
        });

        csvContent += `\nReporting Month,${reportingMonth}\n`;

        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", "Structure_Measures.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    if (loading) return <LoadingNotice title="Loading structure measures data..." />

    const showNoDataOverlay = (!data || !hasData) && !loading && !isMultiSelect

    return (
        <div className="pmh-structure-measures-page" data-export-target={generatePDFIds.exportTarget('structure-measures')}>
            <MultiSelectNotice visible={isMultiSelect} />
            <NoDataOverlay visible={showNoDataOverlay} />

          <div ref={tableContainerRef} >
            <div className="page-header">
                <div className="header-left">
                    <h1 className="page-title">Structure Measures</h1>
                    <p className="reporting-month">Reporting Month: {reportingMonth || "NA"}</p>
                </div>

                <div className="header-right">
                    <div className="legend-bar" style={{ display: 'flex', gap: '20px', alignItems: 'center', padding: '10px', backgroundColor: '#fff', border: '1px solid #e6eef5', borderRadius: '6px', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.04)', fontSize: '12px', fontWeight: '500' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span className="legend-dot green"></span>
                            <span style={{ margin: 0, padding: 0 }}>In place</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span className="legend-dot yellow"></span>
                            <span style={{ margin: 0, padding: 0 }}>Working on it</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span className="legend-dot orange"></span>
                            <span style={{ margin: 0, padding: 0 }}>Haven't started</span>
                        </div>
                    </div>

                    <div className="menu-container export-menu-container export-ignore" style={{ position: 'relative', padding:"5px" }}>
                        <button className="hamburger" onClick={toggleMenu}>&#9776;</button>
                        <div id="export-menu-structure" className="export-dropdown">
                            <div onClick={() => { handleDownloadSVG(); closeMenu(); }}>Download SVG</div>
                            <div onClick={() => { handleDownloadPNG(); closeMenu(); }}>Download PNG</div>
                            <div onClick={() => { handleDownloadCSV(); closeMenu(); }}>Download CSV</div>
                        </div>
                    </div>
                </div>
            </div>

            {!isMultiSelect && !showNoDataOverlay && (
                <div className="table-container">
                    <table id={generatePDFIds.table('structure')}>
                        <thead>
                            <tr>
                                <th style={{ width: '18%' }}>Key Drivers</th>
                                <th style={{ width: '20%' }}>Key System Changes</th>
                                <th>Description</th>
                                <th style={{ width: '9%' }}>Status</th>
                            </tr>
                        </thead>
                        <tbody>                            <tr>
                                <td className="main-driver" rowSpan={3}>Provider Education & Engagement</td>
                                <td className="sub-driver">OB Provider Education</td>
                                <td>Educate all OB clinical staff caring for pregnant/postpartum patients on PMH conditions, screening, treatment, referral processes, hotline resources, and trauma informed care.</td>
                                <td className="status-cell"><div className="status-inner"><span className={`status-dot ${getStatusClass('ob_education')}`}></span></div></td>
                            </tr>
                            <tr>
                                <td className="sub-driver">ER Provider Education</td>
                                <td>Educate all ER clinical staff caring for pregnant/postpartum patients on PMH conditions, screening, treatment, referral processes, hotline resources, and trauma informed care.</td>
                                <td className="status-cell"><div className="status-inner"><span className={`status-dot ${getStatusClass('ed_education')}`}></span></div></td>
                            </tr>
                            <tr>
                                <td className="sub-driver">PMH Access Workgroup</td>
                                <td>Create a PMH Access Workgroup with multidisciplinary inpatient and outpatient care team members (OB, ED, Peds, social work, psychiatry, etc.) and community stakeholders to identify barriers and enhance coordination.</td>
                                <td className="status-cell"><div className="status-inner"><span className={`status-dot ${getStatusClass('multidisciplinary_workgroup')}`}></span></div></td>
                            </tr>
                            <tr>
                                <td className="main-driver" rowSpan={3}>Patient & Community Education and Engagement</td>
                                <td className="sub-driver">Patient Education</td>
                                <td>Have a process to provide postpartum patient education on PMH conditions, warning signs, and follow-up options including hotlines/warm lines.</td>
                                <td className="status-cell"><div className="status-inner"><span className={`status-dot ${getStatusClass('patient_education')}`}></span></div></td>
                            </tr>
                            <tr>
                                <td className="sub-driver">Sharing patient education outpatient</td>
                                <td>Have a process to share PMH patient education resources with outpatient clinical settings.</td>
                                <td className="status-cell"><div className="status-inner"><span className={`status-dot ${getStatusClass('share_education')}`}></span></div></td>
                            </tr>
                            <tr>
                                <td className="sub-driver">Patient Partner</td>
                                <td>Identify a patient partner for hospital PMH quality improvement team and involve them in engagement strategies.</td>
                                <td className="status-cell"><div className="status-inner"><span className={`status-dot ${getStatusClass('patient_partner')}`}></span></div></td>
                            </tr>
                            <tr>
                                <td className="main-driver" rowSpan={2}>Screening & Assessment</td>
                                <td className="sub-driver">Screening Workflow</td>
                                <td>Establish protocol and workflow to screen all OB patients for PMH conditions with validated tools during delivery hospitalization.</td>
                                <td className="status-cell"><div className="status-inner"><span className={`status-dot ${getStatusClass('screening')}`}></span></div></td>
                            </tr>
                            <tr>
                                <td className="sub-driver">Sharing screening and assessments outpatient</td>
                                <td>Share validated PMH screening tools and treatment algorithms with outpatient settings (FQHCs, emergency departments, prenatal clinics, etc.).</td>
                                <td className="status-cell"><div className="status-inner"><span className={`status-dot ${getStatusClass('share_screening')}`}></span></div></td>
                            </tr>
                            <tr>
                                <td className="main-driver">Optimizing Treatment</td>
                                <td className="sub-driver">Treatment Process Flow</td>
                                <td>Establish process flow for treating positive PMH screens during delivery hospitalization, including use of IL DocAssist for consultation.</td>
                                <td className="status-cell"><div className="status-inner"><span className={`status-dot ${getStatusClass('treating')}`}></span></div></td>
                            </tr>
                            <tr>
                                <td className="main-driver" rowSpan={2}>Linkage to Resources & Treatment</td>
                                <td className="sub-driver">Linkage To Follow-up Process Flow</td>
                                <td>Establish process for referral and linkage to behavioral health services for positive PMH screens during delivery, using IL DocAssist and IL MOMS Line.</td>
                                <td className="status-cell"><div className="status-inner"><span className={`status-dot ${getStatusClass('referral_to_link')}`}></span></div></td>
                            </tr>
                            <tr>
                                <td className="sub-driver">Innovative Strategies</td>
                                <td>Identify and implement innovative strategies to address barriers to behavioral health follow-up care for patients with both private and Medicaid insurance.</td>
                                <td className="status-cell"><div className="status-inner"><span className={`status-dot ${getStatusClass('innovative_strategies')}`}></span></div></td>
                            </tr>
                            <tr>
                                <td className="main-driver" rowSpan={3}>Optimize SUD Care</td>
                                <td className="sub-driver">SUD Treatment Linkage OB</td>
                                <td>Establish process for linkage to recovery treatment services for positive perinatal SUD screens in OB settings, including utilization of IL DocAssist and MAR Now.</td>
                                <td className="status-cell"><div className="status-inner"><span className={`status-dot ${getStatusClass('ob_sud_linkage')}`}></span></div></td>
                            </tr>
                            <tr>
                                <td className="sub-driver">SUD Treatment Linkage ER</td>
                                <td>Establish process for linkage to recovery treatment services for positive perinatal SUD screens in ER settings, including utilization of IL DocAssist and MAR Now.</td>
                                <td className="status-cell"><div className="status-inner"><span className={`status-dot ${getStatusClass('er_sud_linkage')}`}></span></div></td>
                            </tr>
                            <tr>
                                <td className="sub-driver">DOPP Participation</td>
                                <td>Improve risk reduction for SUD through participation in DOPP and distribution of naloxone/Narcan kits for pregnant/postpartum patients at risk of overdose.</td>
                                <td className="status-cell"><div className="status-inner"><span className={`status-dot ${getStatusClass('dopp')}`}></span></div></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            )}
             </div>
        </div>
    )
}

export default StructureMeasures
